/*
 * Sliders of the product page (snippets/ts-match-slider.liquid): "Pair with" (shampoos and conditioners: the matching conditioner or
 * shampoo of the same line, same size first) and "Other sizes" (every product: the same product in other sizes). A section with nothing
 * to show stays hidden.
 *
 * The products are found with the store's search suggestions (same brand and product line). A product line is the product name without
 * the size (after " - ") and without the words shampoo / conditioner.
 */
(function () {
  if (customElements.get('ts-match-slider')) return;

  var MAX_CARDS = 8;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[‘’′]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  function baseOf(title) {
    return norm(String(title || '').split(' - ')[0]);
  }

  function sizeOf(title) {
    var parts = String(title || '').split(' - ');
    return parts.length > 1 ? norm(parts.slice(1).join(' - ')) : '';
  }

  // shampoo / conditioner words in English and French ("shampoing" is the French spelling; "après-shampoing" contains it, so the
  // conditioner words are taken out before the shampoo words are looked for)
  var SH = /\b(shampoo|shampooing|shampoing)\b/;
  var CO = /\b(conditioner|revitalisant|apr\u00e8s-shampo{1,2}ing|apres-shampo{1,2}ing)\b/;

  function kindOf(base) {
    var co = CO.test(base);
    var sh = SH.test(base.replace(new RegExp(CO.source, 'g'), ' '));
    if (sh && co) return 'both';
    if (sh) return 'shampoo';
    if (co) return 'conditioner';
    return 'other';
  }

  function lineOf(base) {
    return base
      .replace(new RegExp(CO.source, 'g'), '')
      .replace(new RegExp(SH.source, 'g'), '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function money(amount, root) {
    var n = Number(amount).toFixed(2);
    if (root.dataset.locale === 'fr') return n.replace('.', ',') + ' $';
    return 'C$' + n;
  }

  function imageUrl(url) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=420';
  }

  class TsMatchSlider extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.load();
    }

    suggest(query) {
      var url =
        this.matchRoot +
        'search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide';
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          return (data && data.resources && data.resources.results && data.resources.results.products) || [];
        })
        .catch(function () {
          return [];
        });
    }

    // The matching is done on the ENGLISH names: the French titles of a pair are translated one by one and do not match each other
    // ("Shampooing Sheer Hydration ..." and "Après-shampoing hydratant léger ..."). On a French page the English title comes from the
    // product's data in the default language, and the cards are then filled in with the French details.
    async load() {
      var d = this.dataset;
      var localized = d.root !== '/';
      var curTitle = d.title;
      this.matchRoot = d.root;
      if (localized) {
        this.matchRoot = '/';
        var en = await fetch('/products/' + encodeURIComponent(d.handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          });
        if (en && en.title) curTitle = en.title;
      }
      var curBase = baseOf(curTitle);
      var curKind = kindOf(curBase);
      var curLine = lineOf(curBase);
      var curSize = sizeOf(curTitle);
      if (!curLine) return;

      var queries = [curLine];
      if (curKind === 'shampoo') queries.push(curLine + ' conditioner');
      else if (curKind === 'conditioner') queries.push(curLine + ' shampoo');
      else if (curBase !== curLine) queries.push(curBase);

      var lists = await Promise.all(queries.map(this.suggest, this));
      var seen = {};
      var counterparts = [];
      var sizes = [];
      var wanted = curKind === 'shampoo' ? 'conditioner' : curKind === 'conditioner' ? 'shampoo' : '';

      lists.forEach(function (list) {
        list.forEach(function (p) {
          if (!p || seen[p.handle] || p.handle === d.handle || p.available === false) return;
          if (norm(p.vendor) !== norm(d.vendor)) return;
          var base = baseOf(p.title);
          if (lineOf(base) !== curLine) return;
          var kind = kindOf(base);
          seen[p.handle] = true;
          if (base === curBase) sizes.push(p);
          else if (wanted && kind === wanted) counterparts.push(p);
        });
      });

      counterparts.sort(function (a, b) {
        var as = sizeOf(a.title) === curSize ? 0 : 1;
        var bs = sizeOf(b.title) === curSize ? 0 : 1;
        return as - bs || a.title.localeCompare(b.title);
      });
      sizes.sort(function (a, b) {
        return a.title.localeCompare(b.title);
      });

      var pairs = counterparts.slice(0, MAX_CARDS);
      var others = sizes.slice(0, MAX_CARDS);
      if (!pairs.length && !others.length) return;
      if (localized) {
        pairs = await this.localize(pairs);
        others = await this.localize(others);
      }

      this.fill('pairs', pairs);
      this.fill('sizes', others);
      this.hidden = false;
      this.querySelectorAll('[data-ts-block]').forEach(this.wireArrows, this);
    }

    // the same products with their details in the page's language (title, picture, price), shaped like a search suggestion
    localize(list) {
      var root = this.dataset.root;
      return Promise.all(
        list.map(function (p) {
          return fetch(root + 'products/' + encodeURIComponent(p.handle) + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) {
              return r.ok ? r.json() : null;
            })
            .then(function (j) {
              if (!j) return p;
              return {
                id: j.id,
                handle: j.handle,
                vendor: j.vendor,
                title: j.title,
                price: j.price / 100,
                compare_at_price_max: (j.compare_at_price || 0) / 100,
                featured_image: { url: j.featured_image },
                image: j.featured_image,
                available: j.available,
              };
            })
            .catch(function () {
              return p;
            });
        })
      );
    }

    // one section of the slider: its cards, or hidden when there are none
    fill(name, products) {
      var block = this.querySelector('[data-ts-block="' + name + '"]');
      if (!block || !products.length) return;
      block.querySelector('[data-ts-track]').innerHTML = products.map(this.cardHTML, this).join('');
      block.hidden = false;
    }

    cardHTML(p) {
      var d = this.dataset;
      var price = Number(p.price);
      var compare = Number(p.compare_at_price_max);
      var onSale = compare && compare > price;
      var url = d.root + 'products/' + p.handle;
      var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
      return (
        '<li class="ts-match__card">' +
        '<div class="ts-match__media">' +
        '<a class="ts-match__imglink" href="' + esc(url) + '" tabindex="-1" aria-hidden="true"><span class="ts-match__img">' +
        (onSale ? '<span class="ts-match__badge">' + esc(d.saleLabel) + '</span>' : '') +
        (img ? '<img src="' + esc(imageUrl(img)) + '" alt="" loading="lazy" width="420">' : '') +
        '</span></a>' +
        '<ts-wishlist-button class="ts-wishlist-card" data-product-id="' + esc(p.id) + '" data-product-handle="' + esc(p.handle) +
        '" data-label-add="' + esc(d.labelWishAdd) + '" data-label-remove="' + esc(d.labelWishRemove) + '">' +
        '<button type="button" class="ts-wishlist-card__button" aria-pressed="false" aria-label="' + esc(d.labelWishAdd) + '">' +
        '<svg class="ts-heart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.5 4.04 3 5.5l7 7Z"/></svg>' +
        '</button></ts-wishlist-button>' +
        '</div>' +
        '<a class="ts-match__text" href="' + esc(url) + '">' +
        '<span class="ts-match__vendor">' + esc(p.vendor) + '</span>' +
        '<span class="ts-match__title">' + esc(p.title) + '</span>' +
        '<span class="ts-match__price">' + esc(money(price, this)) +
        (onSale ? ' <s>' + esc(money(compare, this)) + '</s>' : '') + '</span>' +
        '</a></li>'
      );
    }

    wireArrows(block) {
      if (block.hidden) return;
      var track = block.querySelector('[data-ts-track]');
      var prev = block.querySelector('[data-ts-prev]');
      var next = block.querySelector('[data-ts-next]');
      var step = function () {
        var card = track.querySelector('.ts-match__card');
        return card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
      };
      var update = function () {
        var max = track.scrollWidth - track.clientWidth;
        prev.hidden = track.scrollLeft <= 2;
        next.hidden = max <= 2 || track.scrollLeft >= max - 2;
      };
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      requestAnimationFrame(update);
    }
  }

  customElements.define('ts-match-slider', TsMatchSlider);
})();
